import React, { useCallback, useState } from "react";
import { MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text, mergeStyleSets } from "@fluentui/react";
import { useHistory, useLocation } from "react-router-dom";
import useAxios from "axios-hooks";
import { format, parseISO } from "date-fns";
import { axiosInstance } from "../../../../api/config";
import env from "../../../../env";
import Toast from "../../../Forms/Toast";
import FeatureSummary from "../../../Util/FeatureSummary";
import MemberSearch from "./MemberSearch";
import StreakCalendar from "./StreakCalendar";
import StreakPeriodDialog from "./StreakPeriodDialog";
import { STATUS, extractErrorMessage } from "./streakUtils";

const { REACT_APP_API_BASE_URI } = env;

const classes = mergeStyleSets({
    stat: {
        border: "1px solid #e1dfdd",
        borderRadius: 4,
        padding: "8px 14px",
        minWidth: 110,
        background: "#faf9f8",
    },
    statLabel: {
        fontSize: 12,
        color: "#605e5c",
    },
    statValue: {
        fontSize: 18,
        fontWeight: 600,
    },
});

const Stat = ({ label, value }) => (
    <div className={classes.stat}>
        <div className={classes.statLabel}>{label}</div>
        <div className={classes.statValue}>{value}</div>
    </div>
);

const formatOffset = (mins) => {
    const sign = mins < 0 ? "-" : "+";
    const abs = Math.abs(mins);
    return `UTC${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${String(abs % 60).padStart(2, "0")}` : ""}`;
};

const StreakRepairPage = () => {
    const location = useLocation();
    const routerHistory = useHistory();
    const initialMemberId = new URLSearchParams(location.search).get("memberId");

    const [member, setMember] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [toastMessage, setToastMessage] = useState("");

    const [{ data: history, loading, error }, fetchHistory] = useAxios(
        { baseURL: REACT_APP_API_BASE_URI },
        { axios: axiosInstance, manual: true }
    );

    const loadHistory = useCallback(
        (memberId) => fetchHistory({ url: `2020-05-05/internal/streak/${memberId}/history` }).catch(() => {}),
        [fetchHistory]
    );

    const selectMember = (selected) => {
        setMember(selected);
        setSelectedPeriod(null);
        routerHistory.replace({ search: `?memberId=${selected.memberID}` });
        loadHistory(selected.memberID);
    };

    const handleChanged = (message) => {
        setSelectedPeriod(null);
        setToastMessage(message);
        loadHistory(member.memberID);
    };

    const closeToast = useCallback(() => setToastMessage(""), []);

    const isCurrentMember = history && member && history.memberId === member.memberID;
    const missedCount = isCurrentMember ? history.periods.filter((p) => p.status === STATUS.MISSED).length : 0;
    const patchedCount = isCurrentMember ? history.periods.filter((p) => p.patches.length > 0).length : 0;

    return (
        <div style={{ padding: "0 20px 40px 20px" }}>
            <h3>Streak Repair</h3>
            <Stack tokens={{ childrenGap: 20 }}>
                <MemberSearch initialTerm={initialMemberId} onSelect={selectMember} />

                {member && (
                    <Stack tokens={{ childrenGap: 16 }}>
                        <Text variant="large">
                            <strong>{`${member.first || ""} ${member.last || ""}`.trim() || "Member"}</strong>
                            {` · #${member.memberID} · ${member.username || ""} · ${member.cb || ""}`}
                        </Text>

                        {loading && !isCurrentMember && <Spinner size={SpinnerSize.large} label="Loading streak history..." />}

                        {!loading && error && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {extractErrorMessage(error, "Unable to load this member's streak history.")}
                            </MessageBar>
                        )}

                        {!error && isCurrentMember && (
                            <>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
                                    <Stat label="Cadence" value={history.streakCadence} />
                                    <Stat label="Streak goal set" value={format(parseISO(history.goalSetDate), "MMM d, yyyy")} />
                                    <Stat label="Current streak" value={history.currentStreak} />
                                    <Stat label={`Missed ${history.streakCadence}s`} value={missedCount} />
                                    <Stat label={`Patched ${history.streakCadence}s`} value={patchedCount} />
                                    <Stat label="Member time zone" value={formatOffset(history.timeZoneOffsetMins)} />
                                </Stack>

                                {history.periods.length > 0 ? (
                                    <StreakCalendar history={history} onSelectPeriod={setSelectedPeriod} />
                                ) : (
                                    <MessageBar>No streak periods to show yet.</MessageBar>
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </Stack>

            {isCurrentMember && (
                <StreakPeriodDialog
                    memberId={member.memberID}
                    history={history}
                    period={selectedPeriod}
                    onDismiss={() => setSelectedPeriod(null)}
                    onChanged={handleChanged}
                />
            )}

            <Toast show={!!toastMessage} message={toastMessage} onClose={closeToast} />

            <FeatureSummary
                label="About Streak Repair"
                summary="Look up a member to see every day, week, or month since they set their streak goal. Blue periods had qualifying activity, purple periods were patched, and red periods were missed. Click a red period to insert a manual streak patch. Patches cannot be removed here, so confirm the period before patching."
            />
        </div>
    );
};

export default StreakRepairPage;
